// src/components/frontpage/SuggestPage.jsx
// Public, no-account landing page for the São Martinho directory-suggestion pilot.
// Reached at /suggest (see App.js route detection). The form IS the page — minimal
// chrome, mobile-first, pt-PT facing. Surfaces the EXISTING suggestion pipeline via
// the anon-granted submit_directory_suggestion_public RPC; steward review is the
// firebreak (nothing goes live until Gaby approves).
//
// Boundary is hard-wired to 7089 (São Martinho do Porto) for this pilot page.
// Country-by-country / multi-locality generalisation is deliberately out of scope.

import React, { useState, useEffect } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { submitPublicSuggestion } from '../../data/directoryService';

const BOUNDARY_ID = 7089; // São Martinho do Porto (pilot page — hard-wired)

// Category options mirror SuggestListingModal's `directory` section. Labels are
// reused from the already-translated `frontpage` namespace (not duplicated here).
const CATEGORY_OPTIONS = [
  { value: 'health', labelKey: 'frontpage:dir_health' },
  { value: 'home', labelKey: 'frontpage:dir_home' },
  { value: 'official', labelKey: 'frontpage:dir_official' },
  { value: 'learning', labelKey: 'frontpage:dir_learning' },
  { value: 'food_shopping', labelKey: 'frontpage:dir_food_shopping' },
  { value: 'activities', labelKey: 'frontpage:dir_activities' },
  { value: 'transport', labelKey: 'frontpage:dir_transport' },
  { value: 'eating_drinking', labelKey: 'frontpage:dir_eating_drinking' }
];

// Starting set — codes stored in payload.languages. Labels are endonyms (shown in
// their own language), so they are intentionally NOT translated.
const LANGUAGE_OPTIONS = [
  { code: 'pt', label: 'Português' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Español' },
  { code: 'nl', label: 'Nederlands' }
];

const BLUE = '#024B99';

const SuggestPage = () => {
  const { t } = useTranslation('directory');

  const [form, setForm] = useState({
    category: '',
    name: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    description: ''
  });
  const [langs, setLangs] = useState([]);       // checked language codes
  const [otherLangs, setOtherLangs] = useState(''); // free-text, comma-split on submit
  const [honeypot, setHoneypot] = useState(''); // spam trap — humans never see it
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // On successful submission, show the thank-you briefly, then send the visitor
  // to the OurNet front page (this is a standalone page with no in-app nav).
  useEffect(() => {
    if (!success) return;
    const timer = setTimeout(() => {
      window.location.href = `${window.location.origin}/`;
    }, 2200);
    return () => clearTimeout(timer);
  }, [success]);

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const toggleLang = (code) => {
    setLangs(prev => prev.includes(code) ? prev.filter(c => c !== code) : [...prev, code]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Honeypot filled → almost certainly a bot. Silently drop (show the same
    // thank-you so we don't signal the trap), never hitting the database.
    if (honeypot.trim()) {
      setSuccess(true);
      return;
    }

    if (!form.name.trim()) {
      setError(t('name_required'));
      return;
    }

    setSubmitting(true);
    setError(null);

    // Assemble payload. section is fixed to 'directory' for this page.
    const payload = { section: 'directory', ...form };
    Object.keys(payload).forEach(k => {
      if (payload[k] === '') delete payload[k];
    });

    // Languages: checked codes + any comma-separated "Other" entries.
    const extra = otherLangs
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    const languages = [...langs, ...extra];
    if (languages.length > 0) {
      payload.languages = languages;
    }

    const result = await submitPublicSuggestion({
      boundaryId: BOUNDARY_ID,
      payload,
      note: null
    });

    setSubmitting(false);
    if (result.success) {
      setSuccess(true);
    } else {
      setError(t('error_generic'));
    }
  };

  // ---- styles (inline, OpenSauceSans — the page loads no global chrome) ----
  const pageStyle = {
    minHeight: '100vh',
    background: '#EDF2F8',
    fontFamily: 'OpenSauceSans, sans-serif',
    color: '#1f2937',
    padding: '24px 16px',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  };
  const cardStyle = {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    boxSizing: 'border-box'
  };
  const wordmarkStyle = {
    fontSize: '20px',
    fontWeight: '700',
    color: BLUE,
    marginBottom: '16px',
    letterSpacing: '-0.5px'
  };
  const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontFamily: 'OpenSauceSans, sans-serif',
    fontSize: '15px', // >=16px avoids iOS zoom-on-focus; 15 is a reasonable compromise
    color: '#1f2937',
    outline: 'none',
    boxSizing: 'border-box'
  };
  const labelStyle = {
    fontSize: '13px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '4px',
    display: 'block'
  };
  const fieldWrap = { marginBottom: '14px' };

  if (success) {
    return (
      <div style={pageStyle}>
        <div style={cardStyle}>
          <div style={wordmarkStyle}>OurNet</div>
          <div style={{
            textAlign: 'center',
            padding: '32px 8px',
            color: '#059669',
            fontSize: '17px',
            fontWeight: '600',
            lineHeight: 1.5
          }}>
            {t('thanks')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <div style={cardStyle}>
        <div style={wordmarkStyle}>OurNet</div>

        <h1 style={{ fontSize: '20px', fontWeight: '700', color: BLUE, margin: '0 0 6px' }}>
          {t('suggest_title')}
        </h1>
        <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 20px', lineHeight: 1.5 }}>
          {t('suggest_intro')}
        </p>

        <form onSubmit={handleSubmit}>
          {/* Honeypot — visually hidden, off the tab order. Bots fill it; humans don't. */}
          <input
            type="text"
            name="company"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
          />

          {/* Name (required) */}
          <div style={fieldWrap}>
            <label style={labelStyle}>{t('field_name')} *</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              style={inputStyle}
            />
          </div>

          {/* Category */}
          <div style={fieldWrap}>
            <label style={labelStyle}>{t('field_category')}</label>
            <select
              value={form.category}
              onChange={(e) => handleChange('category', e.target.value)}
              style={inputStyle}
            >
              <option value="">{t('select_category')}</option>
              {CATEGORY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
              ))}
            </select>
          </div>

          {/* Phone */}
          <div style={fieldWrap}>
            <label style={labelStyle}>{t('field_phone')}</label>
            <input type="tel" value={form.phone} onChange={(e) => handleChange('phone', e.target.value)} style={inputStyle} />
          </div>

          {/* Email */}
          <div style={fieldWrap}>
            <label style={labelStyle}>{t('field_email')}</label>
            <input type="email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} style={inputStyle} />
          </div>

          {/* Website */}
          <div style={fieldWrap}>
            <label style={labelStyle}>{t('field_website')}</label>
            <input type="url" value={form.website} onChange={(e) => handleChange('website', e.target.value)} style={inputStyle} />
          </div>

          {/* Address */}
          <div style={fieldWrap}>
            <label style={labelStyle}>{t('field_address')}</label>
            <input type="text" value={form.address} onChange={(e) => handleChange('address', e.target.value)} style={inputStyle} />
          </div>

          {/* Description */}
          <div style={fieldWrap}>
            <label style={labelStyle}>{t('field_description')}</label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
            />
          </div>

          {/* Languages spoken */}
          <div style={fieldWrap}>
            <label style={labelStyle}>{t('languages_heading')}</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 14px', marginBottom: '8px' }}>
              {LANGUAGE_OPTIONS.map(opt => (
                <label key={opt.code} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={langs.includes(opt.code)}
                    onChange={() => toggleLang(opt.code)}
                    style={{ width: '16px', height: '16px', accentColor: BLUE }}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
            <input
              type="text"
              value={otherLangs}
              onChange={(e) => setOtherLangs(e.target.value)}
              placeholder={t('lang_other_placeholder')}
              aria-label={t('lang_other')}
              style={inputStyle}
            />
          </div>

          {/* Error */}
          {error && (
            <div style={{ marginBottom: '12px', padding: '8px 10px', background: '#fef2f2', borderRadius: '6px', fontSize: '13px', color: '#dc2626' }}>
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%',
              padding: '12px',
              border: 'none',
              borderRadius: '6px',
              background: submitting ? '#93c5fd' : BLUE,
              fontFamily: 'OpenSauceSans, sans-serif',
              fontSize: '15px',
              fontWeight: '600',
              color: 'white',
              cursor: submitting ? 'default' : 'pointer',
              marginTop: '4px'
            }}
          >
            {submitting ? t('submitting') : t('submit')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default SuggestPage;
